import { Star, Flag, Paperclip, Bot, Clock, AlertCircle, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { Thread } from '@/types';
import { formatDistanceToNow, parseISO } from 'date-fns';
import clsx from 'clsx';

interface ThreadItemProps {
  thread: Thread;
  domainColor: string;
  statusColor: string;
  priorityColor: string;
  onClick: () => void;
}

export function ThreadItem({ thread, domainColor, statusColor, priorityColor, onClick }: ThreadItemProps) {
  const participantEmails = JSON.parse(thread.participant_emails || '[]');
  const aiTags = JSON.parse(thread.ai_tags || '[]');
  const lastMessageTime = formatDistanceToNow(parseISO(thread.last_message_at), { addSuffix: true });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'urgent': return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'partnership': return <Flag className="w-4 h-4 text-purple-600" />;
      case 'support': return <Bot className="w-4 h-4 text-blue-600" />;
      case 'sales': return <CheckCircle className="w-4 h-4 text-green-600" />;
      default: return <MessageSquare className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full p-4 text-left transition-colors hover:bg-gray-50',
        thread.unread_count > 0 && 'bg-signal-50 border-l-4 border-signal-600',
        thread.status === 'spam' && 'opacity-60'
      )}
      role="listitem"
      aria-label={`${thread.subject} from ${participantEmails[0] || 'Unknown'}`}
    >
      <div className="flex items-start gap-3">
        {/* Priority/Category indicator */}
        <div className="flex flex-col items-center gap-1.5 mt-0.5 flex-shrink-0">
          {thread.priority === 'high' && <Flag className="w-4 h-4 text-red-500" aria-label="High priority" />}
          {thread.category && getCategoryIcon(thread.category)}
          {thread.unread_count > 0 && (
            <span className="w-5 h-5 rounded-full bg-signal-600 text-white text-xs font-medium flex items-center justify-center">
              {thread.unread_count > 9 ? '9+' : thread.unread_count}
            </span>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <h3 className={clsx('font-medium text-gray-900 truncate', thread.unread_count > 0 && 'font-semibold')}>
                {thread.subject || '(no subject)'}
              </h3>
              {thread.ai_summary && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-signal-700 bg-signal-100 flex-shrink-0" aria-label="AI Summary">
                  <Bot className="w-3 h-3" />
                </span>
              )}
            </div>
            <time className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0" dateTime={thread.last_message_at}>
              {lastMessageTime}
            </time>
          </div>

          {/* Sender info */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1.5 flex-wrap">
            <span className="font-medium text-gray-700">{participantEmails[0] || 'Unknown sender'}</span>
            {participantEmails.length > 1 && (
              <span className="text-gray-400">+{participantEmails.length - 1} more</span>
            )}
            <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden="true" />
            <span className={domainColor}><span className="px-2 py-0.5 rounded text-xs font-medium">{thread.domain}</span></span>
            <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden="true" />
            <span className={statusColor}><span className="px-2 py-0.5 rounded text-xs font-medium capitalize">{thread.status.replace('_', ' ')}</span></span>
            <span className="w-1 h-1 rounded-full bg-gray-300" aria-hidden="true" />
            <span className={priorityColor}><span className="px-2 py-0.5 rounded text-xs font-medium capitalize">{thread.priority}</span></span>
          </div>

          {/* AI Tags */}
          {aiTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {aiTags.slice(0, 4).map((tag: string) => (
                <span key={tag} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                  {tag}
                </span>
              ))}
              {aiTags.length > 4 && (
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">
                  +{aiTags.length - 4} more
                </span>
              )}
            </div>
          )}

          {/* AI Summary preview */}
          {thread.ai_summary && (
            <p className="text-sm text-gray-600 line-clamp-2 italic bg-gray-50 p-2 rounded-lg">
              {thread.ai_summary}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}